import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, CreditCard, TrendingUp, Activity, Shield, Settings,
  Search, Filter, Download, Trash2, Ban, Eye, Edit, RefreshCw,
  ChevronDown, ChevronRight, Calendar, Clock, Globe, Smartphone,
  Monitor, FileText, Image, File, Upload, FolderOpen, HardDrive,
  Bell, Lock, Database, BarChart3, PieChart, LineChart, DollarSign,
  UserPlus, UserCheck, UserX, Mail, Phone, MapPin, CheckCircle,
  XCircle, AlertCircle, Loader2, Moon, Sun, Menu, X, ArrowUpRight,
  ArrowDownRight, MoreVertical, Copy, ExternalLink, MessageSquare,
  UserCog, Crown, ShieldCheck, Send, Radio
} from "lucide-react";
import AdminLiveSupport from "../components/AdminLiveSupport";

// ==================== ADMIN DASHBOARD ====================
const AdminDashboardNew = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [darkMode, setDarkMode] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  
  // Dashboard stats
  const [stats, setStats] = useState({
    totalUsers: 0,
    newUsersToday: 0,
    newUsersWeek: 0,
    newUsersMonth: 0,
    activeUsers: 0,
    premiumUsers: 0,
    totalRevenue: 0,
    revenueToday: 0,
    revenueMonth: 0,
    revenueYear: 0,
    totalPayments: 0,
    conversionRate: 0
  });
  
  // Users management
  const [users, setUsers] = useState([]);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [userFilter, setUserFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Payments
  const [payments, setPayments] = useState([]);
  const [paymentsPage, setPaymentsPage] = useState(1);
  
  // Storage
  const [storageStats, setStorageStats] = useState({ used: 0, total: 0, files: 0 });
  const [files, setFiles] = useState([]);
  
  // Activity logs
  const [activityLogs, setActivityLogs] = useState([]);
  
  // Support tickets
  const [tickets, setTickets] = useState([]);
  const [ticketsTotal, setTicketsTotal] = useState(0);
  const [ticketFilter, setTicketFilter] = useState("all");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketResponse, setTicketResponse] = useState("");
  
  // Roles management
  const [admins, setAdmins] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [roleSearch, setRoleSearch] = useState("");
  
  // Check admin access
  useEffect(() => {
    if (!user?.is_admin) {
      toast.error("Accès non autorisé");
      navigate("/dashboard");
    }
  }, [user, navigate]);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    
    try {
      const [statsRes, usersRes, paymentsRes, storageRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users?page=1&limit=10`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/subscriptions/transactions?page=1&limit=10`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/storage/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setUsersTotal(usersRes.data.total || 0);
      setPayments(paymentsRes.data.transactions || []);
      setStorageStats(storageRes.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Fetch users with filters
  const fetchUsers = useCallback(async (page = 1, search = "", filter = "all") => {
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append("search", search);
      if (filter !== "all") params.append("filter", filter);
      
      const res = await axios.get(`${API}/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.users || []);
      setUsersTotal(res.data.total || 0);
    } catch (error) {
      toast.error("Erreur lors du chargement des utilisateurs");
    }
  }, [token]);

  // User actions
  const handleBanUser = async (userId, banned) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/ban`, { banned }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(banned ? "Utilisateur banni" : "Utilisateur débanni");
      fetchUsers(usersPage, userSearch, userFilter);
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Supprimer cet utilisateur définitivement ?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Utilisateur supprimé");
      fetchUsers(usersPage, userSearch, userFilter);
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const exportUsersCSV = async () => {
    try {
      const res = await axios.get(`${API}/admin/users/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `users_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export CSV téléchargé");
    } catch (error) {
      toast.error("Erreur export");
    }
  };

  // Fetch support tickets
  const fetchTickets = useCallback(async (status = "all") => {
    try {
      const params = new URLSearchParams({ page: 1, limit: 50 });
      if (status !== "all") params.append("status", status);
      
      const res = await axios.get(`${API}/admin/support/tickets?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(res.data.tickets || []);
      setTicketsTotal(res.data.total || 0);
    } catch (error) {
      console.error("Erreur tickets:", error);
    }
  }, [token]);

  // Respond to ticket
  const handleRespondTicket = async (ticketId) => {
    if (!ticketResponse.trim()) return;
    try {
      await axios.post(`${API}/admin/support/tickets/${ticketId}/respond?response=${encodeURIComponent(ticketResponse)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Réponse envoyée");
      setTicketResponse("");
      setSelectedTicket(null);
      fetchTickets(ticketFilter);
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    }
  };

  // Fetch admins and moderators
  const fetchAdmins = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/roles/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmins(res.data.admins || []);
    } catch (error) {
      console.error("Erreur admins:", error);
    }
  }, [token]);

  // Search users for role assignment
  const searchUsersForRole = async (search) => {
    if (!search || search.length < 2) {
      setAllUsers([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/admin/users?search=${search}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(res.data.users || []);
    } catch (error) {
      console.error("Erreur recherche:", error);
    }
  };

  // Assign role
  const handleAssignRole = async (userId, role) => {
    try {
      await axios.post(`${API}/admin/roles/assign`, { user_id: userId, role }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Rôle ${role} assigné`);
      fetchAdmins();
      setRoleSearch("");
      setAllUsers([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  // Revoke role
  const handleRevokeRole = async (userId) => {
    if (!window.confirm("Révoquer les droits admin de cet utilisateur ?")) return;
    try {
      await axios.post(`${API}/admin/roles/revoke/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Droits révoqués");
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  // Load tickets and admins when tabs change
  useEffect(() => {
    if (activeTab === "support") {
      fetchTickets(ticketFilter);
    } else if (activeTab === "roles") {
      fetchAdmins();
    }
  }, [activeTab, ticketFilter, fetchTickets, fetchAdmins]);

  // Navigation tabs
  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "users", label: "Utilisateurs", icon: Users },
    { id: "payments", label: "Paiements", icon: CreditCard },
    { id: "live-support", label: "Live Support", icon: Radio, highlight: true },
    { id: "support", label: "Tickets", icon: MessageSquare },
    { id: "roles", label: "Rôles", icon: UserCog },
    { id: "storage", label: "Stockage", icon: HardDrive },
    { id: "analytics", label: "Analytics", icon: LineChart },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "security", label: "Sécurité", icon: Shield },
    { id: "settings", label: "Paramètres", icon: Settings }
  ];

  const bgClass = darkMode ? "bg-zinc-950" : "bg-gray-50";
  const cardClass = darkMode ? "bg-zinc-900 border-zinc-800" : "bg-white border-gray-200";
  const textClass = darkMode ? "text-white" : "text-gray-900";
  const mutedClass = darkMode ? "text-zinc-400" : "text-gray-500";

  if (loading) {
    return (
      <div className={`min-h-screen ${bgClass} flex items-center justify-center`}>
        <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bgClass} ${textClass}`}>
      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-full ${sidebarOpen ? 'w-64' : 'w-20'} ${cardClass} border-r z-50 transition-all duration-300`}>
        <div className="p-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <img src="/logo-header.png" alt="Logo" className="w-10 h-10" />
            {sidebarOpen && <span className="font-bold text-lg">Admin Panel</span>}
          </div>
        </div>
        
        <nav className="p-4 space-y-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === tab.id 
                  ? 'bg-pink-500 text-white' 
                  : `${mutedClass} hover:bg-zinc-800`
              }`}
            >
              <tab.icon size={20} />
              {sidebarOpen && <span>{tab.label}</span>}
            </button>
          ))}
        </nav>
        
        <div className="absolute bottom-4 left-4 right-4">
          <button
            onClick={() => setDarkMode(!darkMode)}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl ${cardClass} border`}
          >
            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            {sidebarOpen && <span>{darkMode ? "Mode clair" : "Mode sombre"}</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className={`${sidebarOpen ? 'ml-64' : 'ml-20'} transition-all duration-300`}>
        {/* Header */}
        <header className={`sticky top-0 z-40 ${cardClass} border-b px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-zinc-800 rounded-lg">
                <Menu size={20} />
              </button>
              <h1 className="text-2xl font-bold capitalize">{activeTab}</h1>
            </div>
            <div className="flex items-center gap-4">
              <button onClick={fetchDashboardData} className="p-2 hover:bg-zinc-800 rounded-lg">
                <RefreshCw size={20} />
              </button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-pink-500 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{user?.full_name?.[0] || 'A'}</span>
                </div>
                <span className="font-medium">{user?.full_name || 'Admin'}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="p-6">
          <AnimatePresence mode="wait">
            {activeTab === "dashboard" && (
              <DashboardTab 
                stats={stats} 
                darkMode={darkMode} 
                cardClass={cardClass} 
                mutedClass={mutedClass}
              />
            )}
            {activeTab === "users" && (
              <UsersTab
                users={users}
                total={usersTotal}
                page={usersPage}
                setPage={setUsersPage}
                search={userSearch}
                setSearch={setUserSearch}
                filter={userFilter}
                setFilter={setUserFilter}
                fetchUsers={fetchUsers}
                onBan={handleBanUser}
                onDelete={handleDeleteUser}
                onExport={exportUsersCSV}
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}
                darkMode={darkMode}
                cardClass={cardClass}
                mutedClass={mutedClass}
                token={token}
              />
            )}
            {activeTab === "payments" && (
              <PaymentsTab
                payments={payments}
                darkMode={darkMode}
                cardClass={cardClass}
                mutedClass={mutedClass}
                token={token}
              />
            )}
            {activeTab === "live-support" && (
              <LiveSupportTab 
                token={token}
                darkMode={darkMode}
                cardClass={cardClass}
              />
            )}
            {activeTab === "support" && (
              <SupportTab
                tickets={tickets}
                total={ticketsTotal}
                filter={ticketFilter}
                setFilter={setTicketFilter}
                selectedTicket={selectedTicket}
                setSelectedTicket={setSelectedTicket}
                ticketResponse={ticketResponse}
                setTicketResponse={setTicketResponse}
                onRespond={handleRespondTicket}
                fetchTickets={fetchTickets}
                darkMode={darkMode}
                cardClass={cardClass}
                mutedClass={mutedClass}
              />
            )}
            {activeTab === "roles" && (
              <RolesTab
                admins={admins}
                allUsers={allUsers}
                roleSearch={roleSearch}
                setRoleSearch={setRoleSearch}
                searchUsersForRole={searchUsersForRole}
                onAssignRole={handleAssignRole}
                onRevokeRole={handleRevokeRole}
                currentUser={user}
                darkMode={darkMode}
                cardClass={cardClass}
                mutedClass={mutedClass}
              />
            )}
            {activeTab === "storage" && (
              <StorageTab
                stats={storageStats}
                darkMode={darkMode}
                cardClass={cardClass}
                mutedClass={mutedClass}
                token={token}
              />
            )}
            {activeTab === "analytics" && (
              <AnalyticsTab
                stats={stats}
                darkMode={darkMode}
                cardClass={cardClass}
                mutedClass={mutedClass}
              />
            )}
            {activeTab === "notifications" && (
              <NotificationsTab
                darkMode={darkMode}
                cardClass={cardClass}
                mutedClass={mutedClass}
              />
            )}
            {activeTab === "security" && (
              <SecurityTab
                darkMode={darkMode}
                cardClass={cardClass}
                mutedClass={mutedClass}
                token={token}
              />
            )}
            {activeTab === "settings" && (
              <SettingsTab
                darkMode={darkMode}
                cardClass={cardClass}
                mutedClass={mutedClass}
              />
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

// ==================== DASHBOARD TAB ====================
const DashboardTab = ({ stats, darkMode, cardClass, mutedClass }) => {
  const statCards = [
    { label: "Total Utilisateurs", value: stats.totalUsers || 0, icon: Users, color: "blue", change: "+12%" },
    { label: "Nouveaux Aujourd'hui", value: stats.newUsersToday || 0, icon: UserPlus, color: "green", change: "+5%" },
    { label: "Cette Semaine", value: stats.newUsersWeek || 0, icon: Calendar, color: "purple", change: "+18%" },
    { label: "Ce Mois", value: stats.newUsersMonth || 0, icon: TrendingUp, color: "pink", change: "+25%" },
    { label: "Utilisateurs Premium", value: stats.premiumUsers || 0, icon: Shield, color: "amber", change: "+8%" },
    { label: "Revenus Total", value: `${(stats.totalRevenue || 0).toFixed(2)}€`, icon: DollarSign, color: "emerald", change: "+15%" },
    { label: "Revenus Aujourd'hui", value: `${(stats.revenueToday || 0).toFixed(2)}€`, icon: CreditCard, color: "cyan", change: "+3%" },
    { label: "Taux Conversion", value: `${(stats.conversionRate || 0).toFixed(1)}%`, icon: Activity, color: "rose", change: "+2%" }
  ];

  const colorMap = {
    blue: "from-blue-500 to-blue-600",
    green: "from-green-500 to-green-600",
    purple: "from-purple-500 to-purple-600",
    pink: "from-pink-500 to-pink-600",
    amber: "from-amber-500 to-amber-600",
    emerald: "from-emerald-500 to-emerald-600",
    cyan: "from-cyan-500 to-cyan-600",
    rose: "from-rose-500 to-rose-600"
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className={`${cardClass} border rounded-2xl p-6`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className={`text-sm ${mutedClass}`}>{stat.label}</p>
                <p className="text-3xl font-bold mt-1">{stat.value}</p>
                <div className="flex items-center gap-1 mt-2 text-green-500 text-sm">
                  <ArrowUpRight size={14} />
                  <span>{stat.change}</span>
                </div>
              </div>
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorMap[stat.color]} flex items-center justify-center`}>
                <stat.icon size={24} className="text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className={`${cardClass} border rounded-2xl p-6`}>
          <h3 className="text-lg font-bold mb-4">Revenus Mensuels</h3>
          <div className="h-64 flex items-end justify-between gap-2">
            {[45, 72, 38, 95, 60, 85, 50, 78, 92, 65, 88, 100].map((height, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-2">
                <div 
                  className="w-full bg-gradient-to-t from-pink-500 to-pink-400 rounded-t-lg transition-all hover:from-pink-600 hover:to-pink-500"
                  style={{ height: `${height}%` }}
                />
                <span className={`text-xs ${mutedClass}`}>{['J','F','M','A','M','J','J','A','S','O','N','D'][i]}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Users Chart */}
        <div className={`${cardClass} border rounded-2xl p-6`}>
          <h3 className="text-lg font-bold mb-4">Inscriptions Hebdomadaires</h3>
          <div className="h-64 flex items-end justify-between gap-4">
            {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day, i) => (
              <div key={day} className="flex-1 flex flex-col items-center gap-2">
                <div 
                  className="w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-t-lg"
                  style={{ height: `${[65, 85, 45, 90, 70, 40, 55][i]}%` }}
                />
                <span className={`text-xs ${mutedClass}`}>{day}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Device Stats */}
        <div className={`${cardClass} border rounded-2xl p-6`}>
          <h3 className="text-lg font-bold mb-4">Appareils</h3>
          <div className="space-y-4">
            {[
              { icon: Smartphone, label: "Mobile", value: "62%", color: "pink" },
              { icon: Monitor, label: "Desktop", value: "31%", color: "blue" },
              { icon: Globe, label: "Tablette", value: "7%", color: "purple" }
            ].map(item => (
              <div key={item.label} className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg bg-${item.color}-500/20 flex items-center justify-center`}>
                  <item.icon size={20} className={`text-${item.color}-500`} />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span>{item.label}</span>
                    <span className="font-bold">{item.value}</span>
                  </div>
                  <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div className={`h-full bg-${item.color}-500`} style={{ width: item.value }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Pages */}
        <div className={`${cardClass} border rounded-2xl p-6`}>
          <h3 className="text-lg font-bold mb-4">Pages Populaires</h3>
          <div className="space-y-3">
            {[
              { page: "/", views: "12,450" },
              { page: "/premium", views: "8,320" },
              { page: "/dashboard", views: "5,180" },
              { page: "/profile", views: "3,920" },
              { page: "/auth", views: "2,150" }
            ].map((item, i) => (
              <div key={item.page} className="flex items-center justify-between py-2 border-b border-zinc-800 last:border-0">
                <div className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-pink-500 text-white' : 'bg-zinc-800'}`}>
                    {i + 1}
                  </span>
                  <span className="font-mono text-sm">{item.page}</span>
                </div>
                <span className={mutedClass}>{item.views}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className={`${cardClass} border rounded-2xl p-6`}>
          <h3 className="text-lg font-bold mb-4">Activité Récente</h3>
          <div className="space-y-3">
            {[
              { action: "Nouvel utilisateur", user: "Marie D.", time: "2min" },
              { action: "Paiement Premium", user: "Jean P.", time: "15min" },
              { action: "Inscription", user: "Lucas M.", time: "32min" },
              { action: "Connexion Admin", user: "Samy C.", time: "1h" }
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 py-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <div className="flex-1">
                  <span className="font-medium">{item.action}</span>
                  <span className={`ml-2 ${mutedClass}`}>- {item.user}</span>
                </div>
                <span className={`text-xs ${mutedClass}`}>{item.time}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ==================== USERS TAB ====================
const UsersTab = ({ users, total, page, setPage, search, setSearch, filter, setFilter, fetchUsers, onBan, onDelete, onExport, selectedUser, setSelectedUser, darkMode, cardClass, mutedClass, token }) => {
  useEffect(() => {
    fetchUsers(page, search, filter);
  }, [page, search, filter, fetchUsers]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {/* Toolbar */}
      <div className={`${cardClass} border rounded-2xl p-4`}>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px] relative">
            <Search size={18} className={`absolute left-3 top-1/2 -translate-y-1/2 ${mutedClass}`} />
            <input
              type="text"
              placeholder="Rechercher un utilisateur..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-xl bg-zinc-800 border border-zinc-700 focus:border-pink-500 outline-none"
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 rounded-xl bg-zinc-800 border border-zinc-700"
          >
            <option value="all">Tous</option>
            <option value="premium">Premium</option>
            <option value="banned">Bannis</option>
            <option value="recent">Récents</option>
          </select>
          <button onClick={onExport} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-green-500 hover:bg-green-600 text-white">
            <Download size={18} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className={`${cardClass} border rounded-2xl overflow-hidden`}>
        <table className="w-full">
          <thead className="bg-zinc-800/50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium">Utilisateur</th>
              <th className="px-4 py-3 text-left text-sm font-medium">Email</th>
              <th className="px-4 py-3 text-left text-sm font-medium">Téléphone</th>
              <th className="px-4 py-3 text-left text-sm font-medium">Inscription</th>
              <th className="px-4 py-3 text-left text-sm font-medium">Statut</th>
              <th className="px-4 py-3 text-left text-sm font-medium">Abonnement</th>
              <th className="px-4 py-3 text-right text-sm font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} className="border-t border-zinc-800 hover:bg-zinc-800/30 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                      {user.full_name?.[0] || '?'}
                    </div>
                    <span className="font-medium">{user.full_name || 'N/A'}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm">{user.email}</td>
                <td className="px-4 py-3 text-sm">{user.phone || '-'}</td>
                <td className="px-4 py-3 text-sm">{new Date(user.created_at).toLocaleDateString('fr-FR')}</td>
                <td className="px-4 py-3">
                  {user.is_banned ? (
                    <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-500">Banni</span>
                  ) : user.email_verified ? (
                    <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-500">Actif</span>
                  ) : (
                    <span className="px-2 py-1 rounded-full text-xs bg-yellow-500/20 text-yellow-500">Non vérifié</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {user.is_premium ? (
                    <span className="px-2 py-1 rounded-full text-xs bg-pink-500/20 text-pink-500">Premium</span>
                  ) : (
                    <span className="px-2 py-1 rounded-full text-xs bg-zinc-500/20 text-zinc-400">Gratuit</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => setSelectedUser(user)} className="p-2 hover:bg-zinc-700 rounded-lg" title="Voir détails">
                      <Eye size={16} />
                    </button>
                    <button onClick={() => onBan(user.id, !user.is_banned)} className="p-2 hover:bg-zinc-700 rounded-lg" title={user.is_banned ? "Débannir" : "Bannir"}>
                      <Ban size={16} className={user.is_banned ? "text-green-500" : "text-orange-500"} />
                    </button>
                    <button onClick={() => onDelete(user.id)} className="p-2 hover:bg-zinc-700 rounded-lg text-red-500" title="Supprimer">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {/* Pagination */}
        <div className="px-4 py-3 border-t border-zinc-800 flex items-center justify-between">
          <span className={mutedClass}>{total} utilisateurs</span>
          <div className="flex gap-2">
            <button 
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-3 py-1 rounded-lg bg-zinc-800 disabled:opacity-50"
            >
              Précédent
            </button>
            <span className="px-3 py-1">Page {page}</span>
            <button 
              onClick={() => setPage(page + 1)}
              disabled={users.length < 20}
              className="px-3 py-1 rounded-lg bg-zinc-800 disabled:opacity-50"
            >
              Suivant
            </button>
          </div>
        </div>
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <UserDetailModal user={selectedUser} onClose={() => setSelectedUser(null)} cardClass={cardClass} mutedClass={mutedClass} />
      )}
    </motion.div>
  );
};

// User Detail Modal
const UserDetailModal = ({ user, onClose, cardClass, mutedClass }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`${cardClass} border rounded-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-auto`}
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Détails Utilisateur</h2>
        <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
          <X size={20} />
        </button>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        {[
          { label: "ID", value: user.id },
          { label: "Nom complet", value: user.full_name },
          { label: "Email", value: user.email },
          { label: "Téléphone", value: user.phone || "Non renseigné" },
          { label: "Date inscription", value: new Date(user.created_at).toLocaleString('fr-FR') },
          { label: "Niveau", value: user.level },
          { label: "Notes positives", value: user.positive_ratings },
          { label: "Premium", value: user.is_premium ? "Oui" : "Non" },
          { label: "Plan", value: user.subscription_plan || "Aucun" },
          { label: "Admin", value: user.is_admin ? "Oui" : "Non" }
        ].map(item => (
          <div key={item.label} className="p-3 rounded-xl bg-zinc-800/50">
            <p className={`text-sm ${mutedClass}`}>{item.label}</p>
            <p className="font-medium">{item.value}</p>
          </div>
        ))}
      </div>
    </motion.div>
  </div>
);

// ==================== PAYMENTS TAB ====================
const PaymentsTab = ({ payments, darkMode, cardClass, mutedClass, token }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
    <div className={`${cardClass} border rounded-2xl overflow-hidden`}>
      <table className="w-full">
        <thead className="bg-zinc-800/50">
          <tr>
            <th className="px-4 py-3 text-left text-sm font-medium">Transaction</th>
            <th className="px-4 py-3 text-left text-sm font-medium">Utilisateur</th>
            <th className="px-4 py-3 text-left text-sm font-medium">Plan</th>
            <th className="px-4 py-3 text-left text-sm font-medium">Montant</th>
            <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
            <th className="px-4 py-3 text-left text-sm font-medium">Statut</th>
          </tr>
        </thead>
        <tbody>
          {payments.length === 0 ? (
            <tr><td colSpan={6} className="px-4 py-8 text-center">Aucun paiement</td></tr>
          ) : payments.map(payment => (
            <tr key={payment.id} className="border-t border-zinc-800">
              <td className="px-4 py-3 font-mono text-sm">{payment.id?.slice(0, 8)}...</td>
              <td className="px-4 py-3">{payment.user_id?.slice(0, 8)}...</td>
              <td className="px-4 py-3">{payment.plan_id}</td>
              <td className="px-4 py-3 font-bold">{payment.amount?.toFixed(2)}€</td>
              <td className="px-4 py-3 text-sm">{new Date(payment.created_at).toLocaleDateString('fr-FR')}</td>
              <td className="px-4 py-3">
                <span className={`px-2 py-1 rounded-full text-xs ${
                  payment.payment_status === 'paid' ? 'bg-green-500/20 text-green-500' :
                  payment.payment_status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                  'bg-red-500/20 text-red-500'
                }`}>
                  {payment.payment_status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </motion.div>
);

// ==================== STORAGE TAB ====================
const StorageTab = ({ stats, darkMode, cardClass, mutedClass, token }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className={`${cardClass} border rounded-2xl p-6`}>
        <HardDrive size={24} className="text-blue-500 mb-2" />
        <p className={mutedClass}>Espace utilisé</p>
        <p className="text-2xl font-bold">{((stats.used || 0) / 1024 / 1024).toFixed(2)} MB</p>
      </div>
      <div className={`${cardClass} border rounded-2xl p-6`}>
        <File size={24} className="text-green-500 mb-2" />
        <p className={mutedClass}>Fichiers</p>
        <p className="text-2xl font-bold">{stats.files || 0}</p>
      </div>
      <div className={`${cardClass} border rounded-2xl p-6`}>
        <Database size={24} className="text-purple-500 mb-2" />
        <p className={mutedClass}>Limite</p>
        <p className="text-2xl font-bold">10 GB</p>
      </div>
    </div>
  </motion.div>
);

// ==================== ANALYTICS TAB ====================
const AnalyticsTab = ({ stats, darkMode, cardClass, mutedClass }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
    <div className={`${cardClass} border rounded-2xl p-6`}>
      <h3 className="text-lg font-bold mb-4">Analyse du Trafic</h3>
      <p className={mutedClass}>Statistiques avancées en cours de développement...</p>
    </div>
  </motion.div>
);

// ==================== NOTIFICATIONS TAB ====================
const NotificationsTab = ({ darkMode, cardClass, mutedClass }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
    <div className={`${cardClass} border rounded-2xl p-6`}>
      <h3 className="text-lg font-bold mb-4">Centre de Notifications</h3>
      <p className={mutedClass}>Aucune notification</p>
    </div>
  </motion.div>
);

// ==================== SECURITY TAB ====================
const SecurityTab = ({ darkMode, cardClass, mutedClass, token }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
    <div className={`${cardClass} border rounded-2xl p-6`}>
      <h3 className="text-lg font-bold mb-4">Sécurité & Logs</h3>
      <div className="space-y-4">
        <div className="flex items-center gap-4 p-4 rounded-xl bg-green-500/10 border border-green-500/20">
          <CheckCircle className="text-green-500" />
          <div>
            <p className="font-medium">Authentification JWT active</p>
            <p className={`text-sm ${mutedClass}`}>Tokens sécurisés avec expiration</p>
          </div>
        </div>
        <div className="flex items-center gap-4 p-4 rounded-xl bg-green-500/10 border border-green-500/20">
          <Shield className="text-green-500" />
          <div>
            <p className="font-medium">Chiffrement TLS 1.3</p>
            <p className={`text-sm ${mutedClass}`}>Communications sécurisées</p>
          </div>
        </div>
        <div className="flex items-center gap-4 p-4 rounded-xl bg-green-500/10 border border-green-500/20">
          <Lock className="text-green-500" />
          <div>
            <p className="font-medium">Mots de passe hashés (bcrypt)</p>
            <p className={`text-sm ${mutedClass}`}>Protection des données sensibles</p>
          </div>
        </div>
      </div>
    </div>
  </motion.div>
);

// ==================== SETTINGS TAB ====================
const SettingsTab = ({ darkMode, cardClass, mutedClass }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
    <div className={`${cardClass} border rounded-2xl p-6`}>
      <h3 className="text-lg font-bold mb-4">Paramètres du Site</h3>
      <p className={mutedClass}>Configuration en cours de développement...</p>
    </div>
  </motion.div>
);

// ==================== LIVE SUPPORT TAB (Socket.IO) ====================
const LiveSupportTab = ({ token, darkMode, cardClass }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-[calc(100vh-200px)]">
    <AdminLiveSupport />
  </motion.div>
);

// ==================== SUPPORT TAB ====================
const SupportTab = ({ 
  tickets, total, filter, setFilter, selectedTicket, setSelectedTicket,
  ticketResponse, setTicketResponse, onRespond, fetchTickets, darkMode, cardClass, mutedClass 
}) => {
  const statusColors = {
    pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    resolved: "bg-green-500/20 text-green-400 border-green-500/30"
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`${cardClass} border rounded-2xl p-6`}>
          <MessageSquare size={24} className="text-pink-500 mb-2" />
          <p className={mutedClass}>Total Tickets</p>
          <p className="text-2xl font-bold">{total}</p>
        </div>
        <div className={`${cardClass} border rounded-2xl p-6`}>
          <Clock size={24} className="text-yellow-500 mb-2" />
          <p className={mutedClass}>En attente</p>
          <p className="text-2xl font-bold">{tickets.filter(t => t.status === "pending").length}</p>
        </div>
        <div className={`${cardClass} border rounded-2xl p-6`}>
          <CheckCircle size={24} className="text-green-500 mb-2" />
          <p className={mutedClass}>Résolus</p>
          <p className="text-2xl font-bold">{tickets.filter(t => t.status === "resolved").length}</p>
        </div>
      </div>

      {/* Filters */}
      <div className={`${cardClass} border rounded-2xl p-4`}>
        <div className="flex items-center gap-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-2 text-sm"
          >
            <option value="all">Tous les tickets</option>
            <option value="pending">En attente</option>
            <option value="resolved">Résolus</option>
          </select>
          <button 
            onClick={() => fetchTickets(filter)}
            className="p-2 hover:bg-zinc-800 rounded-lg"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Tickets List */}
      <div className={`${cardClass} border rounded-2xl overflow-hidden`}>
        {tickets.length === 0 ? (
          <div className="p-8 text-center">
            <MessageSquare size={48} className="mx-auto mb-4 text-zinc-600" />
            <p className={mutedClass}>Aucun ticket de support</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {tickets.map((ticket) => (
              <div 
                key={ticket.id}
                className={`p-4 hover:bg-zinc-800/50 cursor-pointer transition-colors ${selectedTicket?.id === ticket.id ? 'bg-zinc-800' : ''}`}
                onClick={() => setSelectedTicket(selectedTicket?.id === ticket.id ? null : ticket)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center">
                      <MessageSquare size={18} className="text-pink-400" />
                    </div>
                    <div>
                      <p className="font-medium">Session: {ticket.session_id?.slice(0, 12)}...</p>
                      <p className={`text-sm ${mutedClass}`}>
                        {new Date(ticket.created_at).toLocaleString('fr-FR')}
                      </p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs border ${statusColors[ticket.status] || statusColors.pending}`}>
                    {ticket.status === "pending" ? "En attente" : "Résolu"}
                  </span>
                </div>
                
                <p className="text-sm text-zinc-300 line-clamp-2">{ticket.user_message}</p>

                {/* Expanded view */}
                {selectedTicket?.id === ticket.id && (
                  <motion.div 
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    className="mt-4 pt-4 border-t border-zinc-800"
                  >
                    {/* Conversation History */}
                    {ticket.conversation_history?.length > 0 && (
                      <div className="mb-4 max-h-60 overflow-y-auto space-y-2">
                        <p className="text-xs text-zinc-500 uppercase mb-2">Historique conversation</p>
                        {ticket.conversation_history.map((msg, idx) => (
                          <div 
                            key={idx}
                            className={`p-3 rounded-lg text-sm ${
                              msg.role === "user" 
                                ? "bg-blue-500/10 border border-blue-500/20" 
                                : "bg-pink-500/10 border border-pink-500/20"
                            }`}
                          >
                            <span className="font-medium">{msg.role === "user" ? "Visiteur" : "IA"}:</span> {msg.content}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Response form */}
                    {ticket.status === "pending" ? (
                      <div className="space-y-3">
                        <textarea
                          value={ticketResponse}
                          onChange={(e) => setTicketResponse(e.target.value)}
                          placeholder="Votre réponse..."
                          rows={3}
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-pink-500"
                        />
                        <button
                          onClick={() => onRespond(ticket.id)}
                          disabled={!ticketResponse.trim()}
                          className="flex items-center gap-2 px-4 py-2 bg-pink-500 text-white rounded-xl hover:bg-pink-600 transition-colors disabled:opacity-50"
                        >
                          <Send size={16} />
                          Envoyer la réponse
                        </button>
                      </div>
                    ) : (
                      <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                        <p className="text-sm font-medium text-green-400">Réponse admin:</p>
                        <p className="text-sm text-zinc-300">{ticket.admin_response}</p>
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ==================== ROLES TAB ====================
const RolesTab = ({ 
  admins, allUsers, roleSearch, setRoleSearch, searchUsersForRole, 
  onAssignRole, onRevokeRole, currentUser, darkMode, cardClass, mutedClass 
}) => {
  const roleIcons = {
    super_admin: Crown,
    admin: ShieldCheck,
    moderator: Shield
  };

  const roleColors = {
    super_admin: "text-amber-400 bg-amber-500/20 border-amber-500/30",
    admin: "text-pink-400 bg-pink-500/20 border-pink-500/30",
    moderator: "text-blue-400 bg-blue-500/20 border-blue-500/30"
  };

  const isSuperAdmin = currentUser?.role === "super_admin" || currentUser?.email === "contact@rateshare.fr";

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Role legend */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`${cardClass} border rounded-2xl p-6`}>
          <div className="flex items-center gap-3 mb-2">
            <Crown className="text-amber-400" size={24} />
            <span className="font-bold">Super Admin</span>
          </div>
          <p className={`text-sm ${mutedClass}`}>Tous les droits, peut nommer d'autres admins</p>
        </div>
        <div className={`${cardClass} border rounded-2xl p-6`}>
          <div className="flex items-center gap-3 mb-2">
            <ShieldCheck className="text-pink-400" size={24} />
            <span className="font-bold">Admin</span>
          </div>
          <p className={`text-sm ${mutedClass}`}>Gestion utilisateurs, contenu, statistiques</p>
        </div>
        <div className={`${cardClass} border rounded-2xl p-6`}>
          <div className="flex items-center gap-3 mb-2">
            <Shield className="text-blue-400" size={24} />
            <span className="font-bold">Modérateur</span>
          </div>
          <p className={`text-sm ${mutedClass}`}>Modération contenu, support messages</p>
        </div>
      </div>

      {/* Add new admin (Super Admin only) */}
      {isSuperAdmin && (
        <div className={`${cardClass} border rounded-2xl p-6`}>
          <h3 className="text-lg font-bold mb-4">Ajouter un administrateur</h3>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
              <input
                type="text"
                value={roleSearch}
                onChange={(e) => {
                  setRoleSearch(e.target.value);
                  searchUsersForRole(e.target.value);
                }}
                placeholder="Rechercher un utilisateur par nom ou email..."
                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl pl-12 pr-4 py-3 focus:outline-none focus:border-pink-500"
              />
            </div>

            {/* Search results */}
            {allUsers.length > 0 && (
              <div className="bg-zinc-800/50 rounded-xl border border-zinc-700 divide-y divide-zinc-700">
                {allUsers.map((user) => (
                  <div key={user.id} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center">
                        <span className="font-bold">{user.full_name?.[0] || '?'}</span>
                      </div>
                      <div>
                        <p className="font-medium">{user.full_name}</p>
                        <p className={`text-sm ${mutedClass}`}>{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onAssignRole(user.id, "moderator")}
                        className="px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg text-sm hover:bg-blue-500/30 transition-colors"
                      >
                        Modérateur
                      </button>
                      <button
                        onClick={() => onAssignRole(user.id, "admin")}
                        className="px-3 py-1.5 bg-pink-500/20 text-pink-400 rounded-lg text-sm hover:bg-pink-500/30 transition-colors"
                      >
                        Admin
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Current admins list */}
      <div className={`${cardClass} border rounded-2xl overflow-hidden`}>
        <div className="p-4 border-b border-zinc-800">
          <h3 className="font-bold">Équipe d'administration ({admins.length})</h3>
        </div>
        
        {admins.length === 0 ? (
          <div className="p-8 text-center">
            <UserCog size={48} className="mx-auto mb-4 text-zinc-600" />
            <p className={mutedClass}>Aucun administrateur configuré</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {admins.map((admin) => {
              const RoleIcon = roleIcons[admin.role] || Shield;
              const roleColor = roleColors[admin.role] || roleColors.moderator;
              
              return (
                <div key={admin.id} className="p-4 flex items-center justify-between hover:bg-zinc-800/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-zinc-700 flex items-center justify-center">
                      <span className="font-bold text-lg">{admin.full_name?.[0] || '?'}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{admin.full_name}</p>
                        <span className={`px-2 py-0.5 rounded-full text-xs border ${roleColor}`}>
                          <RoleIcon size={12} className="inline mr-1" />
                          {admin.role === "super_admin" ? "Super Admin" : admin.role === "admin" ? "Admin" : "Modérateur"}
                        </span>
                      </div>
                      <p className={`text-sm ${mutedClass}`}>{admin.email}</p>
                    </div>
                  </div>
                  
                  {/* Revoke button (only for super admin, can't revoke self) */}
                  {isSuperAdmin && admin.id !== currentUser?.id && admin.role !== "super_admin" && (
                    <button
                      onClick={() => onRevokeRole(admin.id)}
                      className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/30 transition-colors"
                    >
                      <XCircle size={16} className="inline mr-1" />
                      Révoquer
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default AdminDashboardNew;
